import * as Api from '@ViewModels';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { useUserQueries } from '../User/useUserQuery';

/**
 * Resolves the people who granted the current user meeting-admin delegation, plus the `forUserId`
 * that meeting requests need when one of them is the selected host.
 *
 * The grant carries only ids, so display names are fetched up front — but `hasDelegatedAccess`
 * comes straight off the grant, so a picker can enable itself before those names land.
 */
export function useDelegatedMeetingUsers({
	currentUserId,
	impersonationContext,
	selectedUserId,
}: {
	currentUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
	selectedUserId?: string;
}) {
	const userSession = useUserSession();

	const delegatedAccess = impersonationContext?.account?.id
		? impersonationContext.user?.delegatedAccess
		: userSession.user?.delegatedAccess;
	const delegatedUserIds = delegatedAccess?.meetings?.admin ? (delegatedAccess.userIds ?? []) : [];

	const delegatedUserQueries = useUserQueries({ ids: delegatedUserIds, impersonationContext });
	const delegatedUsers = delegatedUserQueries.map(query => query.data).filter((user): user is Api.IUser => !!user?.id);

	// Selecting a delegator runs the request against their meetings; selecting yourself never does.
	// Sent even for account-wide admins who also hold a grant: delegation is what authorizes the
	// write actions, and `scheduler.viewReporting` doesn't imply `scheduler.write`.
	const forUserId =
		selectedUserId && selectedUserId !== currentUserId && delegatedUsers.some(user => user.id === selectedUserId)
			? selectedUserId
			: undefined;

	return { delegatedUsers, forUserId, hasDelegatedAccess: delegatedUserIds.length > 0 };
}
